import React from "react";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
  "#06b6d4", "#f97316", "#14b8a6", "#a855f7", "#e11d48", "#84cc16",
];

/**
 * Constructs to include on the radar.
 * Each needs: questionId to find in constructs, label for axis, scale bounds for normalization.
 * Searches for both likert_battery (uses aggregateStats) and standalone questions (uses stats).
 */
const RADAR_CONSTRUCTS = [
  { questionId: "mc_emotion", label: "Emotionality", min: 1, max: 7 },
  { questionId: "pol", label: "Polarization", min: 1, max: 7 },
  { questionId: "closure", label: "Closure", min: 1, max: 7 },
  { questionId: "mc_outgroup", label: "Adversarial", min: 1, max: 7 },
  { questionId: "emotion_anger", label: "Anger", min: 1, max: 7 },
  { questionId: "mc_ingroup", label: "Ingroup Sol.", min: 1, max: 7 },
  { questionId: "mc_attr", label: "Group Naming", min: 1, max: 7 },
];

/**
 * Radar/spider chart showing condition profiles across key constructs.
 * Normalizes all scales to 0-1 for comparability.
 * Each polygon = one condition, each axis = one construct.
 */
const ConditionRadarChart = ({ constructs, conditions, height = 400 }) => {
  if (!constructs || !conditions || conditions.length === 0) return null;

  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  // Find stats for each radar construct from the constructs data
  const findStats = (questionId) => {
    for (const construct of constructs) {
      for (const q of construct.questions || []) {
        if (q.questionId === questionId) {
          return q.aggregateStats || q.stats;
        }
      }
    }
    return null;
  };

  // Also try to compute thermometer gap
  const thermSupport = findStats("therm_support");
  const thermOppose = findStats("therm_oppose");

  // Build radar data: one entry per construct, with normalized values per condition
  const radarData = [];

  for (const rc of RADAR_CONSTRUCTS) {
    const stats = findStats(rc.questionId);
    if (!stats?.byCondition) continue;

    const entry = { construct: rc.label };
    for (const c of conditions) {
      const val = stats.byCondition[c.conditionId]?.mean;
      if (val !== undefined) {
        // Normalize to 0-1
        entry[c.conditionId] = (val - rc.min) / (rc.max - rc.min);
      } else {
        entry[c.conditionId] = 0;
      }
    }
    radarData.push(entry);
  }

  // Add thermometer gap if available
  if (thermSupport?.byCondition && thermOppose?.byCondition) {
    const entry = { construct: "Therm. Gap" };
    for (const c of conditions) {
      const sup = thermSupport.byCondition[c.conditionId]?.mean || 50;
      const opp = thermOppose.byCondition[c.conditionId]?.mean || 50;
      entry[c.conditionId] = Math.abs(sup - opp) / 100; // Normalize 0-100 gap to 0-1
    }
    radarData.push(entry);
  }

  if (radarData.length < 3) return null;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs shadow-lg max-w-xs">
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }}>
            {conditionLabels[entry.dataKey] || entry.dataKey}: {(entry.value * 100).toFixed(0)}%
          </p>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
        <PolarGrid stroke="#374151" />
        <PolarAngleAxis
          dataKey="construct"
          tick={{ fill: "#d1d5db", fontSize: 11 }}
        />
        <PolarRadiusAxis
          angle={90}
          domain={[0, 1]}
          tick={{ fill: "#6b7280", fontSize: 9 }}
          tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
          tickCount={5}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          formatter={(value) => (
            <span className="text-gray-300 text-xs">
              {conditionLabels[value] || value}
            </span>
          )}
        />
        {conditions.map((c, i) => (
          <Radar
            key={c.conditionId}
            name={c.conditionId}
            dataKey={c.conditionId}
            stroke={CONDITION_COLORS[i % CONDITION_COLORS.length]}
            fill={CONDITION_COLORS[i % CONDITION_COLORS.length]}
            fillOpacity={0.1}
            strokeWidth={2}
          />
        ))}
      </RadarChart>
    </ResponsiveContainer>
  );
};

export default ConditionRadarChart;
